import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft,
  Search,
  Mail,
  Phone,
  Eye,
  Trash2,
  X,
  RefreshCw
} from "lucide-react";
import { contactAPI } from "../services/api";

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to format datetime
const formatDateTime = (dateString) => {
  if (!dateString) return { date: "N/A", time: "" };
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return { date: dateString, time: "" };
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, '0');
  return {
    date: `${day}/${month}/${year}`,
    time: `${formattedHours}:${formattedMinutes} ${ampm}`
  };
};

export function ContactMessages() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Use useCallback to ensure fetchMessages is stable
  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { page, limit };
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      
      const response = await contactAPI.getAll(params);
      
      if (response.success) {
        setMessages(response.data || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 0);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch messages");
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Get unique subjects for filter dropdown
  const subjects = ["all", ...new Set(messages.map(m => m.subject).filter(Boolean))];

  const filteredMessages = messages.filter(msg => {
    const matchesSearch = 
      (msg.name && msg.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (msg.email && msg.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (msg.subject && msg.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (msg.message && msg.message.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesSubject = subjectFilter === "all" || msg.subject === subjectFilter;
    
    return matchesSearch && matchesSubject;
  });

  const handleViewMessage = async (message) => {
    // Show modal first with current data
    setSelectedMessage(message);
    setShowMessageModal(true);
    
    // Get current status (normalize to lowercase)
    const currentStatus = (message.status || '').toString().toLowerCase().trim();
    
    // Only mark as read if NOT already 'read' or 'replied'
    if (currentStatus !== 'read' && currentStatus !== 'replied') {
      try {
        const result = await contactAPI.markAsRead(message.id);
        
        // Update local state immediately
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.id === message.id 
              ? { ...msg, status: 'read' }
              : msg
          )
        );
        
        // Update selected message
        setSelectedMessage(prev => ({ ...prev, status: 'read' }));
      } catch (err) {
        console.error('Error marking as read:', err);
      }
    }
  };

  const handleCloseModal = () => {
    setShowMessageModal(false);
    setSelectedMessage(null);
  };

  const handleDeleteMessage = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await contactAPI.delete(id);
        fetchMessages();
      } catch (err) {
        console.error("Error deleting message:", err);
        alert("Failed to delete message");
      }
    }
  };

  const handleMarkAsReplied = async (id) => {
    try {
      await contactAPI.reply(id, {});
      // Update local state immediately
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === id 
            ? { ...msg, status: 'replied' }
            : msg
        )
      );
      // Update selected message
      if (selectedMessage && selectedMessage.id === id) {
        setSelectedMessage(prev => ({ ...prev, status: 'replied' }));
      }
    } catch (err) {
      console.error("Error marking as replied:", err);
      alert("Failed to mark as replied");
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'unread':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">Unread</span>;
      case 'read':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Read</span>;
      case 'replied':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Replied</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/" className="p-2 hover:bg-gray-100 rounded-lg transition">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[#111111]">Contact Messages</h1>
            <p className="text-gray-600">View messages from the contact form</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-[#C46A1B] mx-auto mb-2" />
            <p className="text-gray-500">Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/" className="p-2 hover:bg-gray-100 rounded-lg transition">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[#111111]">Contact Messages</h1>
            <p className="text-gray-600">View messages from the contact form</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchMessages}
            className="mt-2 px-4 py-2 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/" className="p-2 hover:bg-gray-100 rounded-lg transition">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Contact Messages</h1>
          <p className="text-gray-600">View messages from the contact form</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm flex flex-wrap gap-4">
        {/* Search */}
        <div className="flex-1 flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
          <Search className="w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, email, subject..."
            className="bg-transparent border-none outline-none text-sm w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")} className="p-1 hover:bg-gray-200 rounded">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <select 
          className="px-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-[#C46A1B] focus:border-transparent outline-none"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
        </select>

        {/* Subject Filter Dropdown */}
        <select 
          className="px-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-[#C46A1B] focus:border-transparent outline-none"
          value={subjectFilter}
          onChange={(e) => setSubjectFilter(e.target.value)}
        >
          {subjects.map((subject, index) => (
            <option key={index} value={subject}>
              {subject === "all" ? "All Subjects" : subject}
            </option>
          ))}
        </select>

        {/* Clear Filters */}
        {(searchTerm || statusFilter !== "all" || subjectFilter !== "all") && (
          <button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setSubjectFilter("all");
            }}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
          >
            Clear
          </button>
        )}
      </div>

      {/* Messages Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm text-gray-600">
                <th className="p-4">Date</th>
                <th className="p-4">Name</th>
                <th className="p-4">Subject</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Status</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredMessages.map((msg) => {
                const { date, time } = formatDateTime(msg.created_at);
                return (
                  <tr key={msg.id} className="hover:bg-gray-50 transition">
                    <td className="p-4 text-sm whitespace-nowrap">
                      <div>{date}</div>
                    </td>
                    <td className="p-4">
                      <div className="font-medium">{msg.name}</div>
                      <div className="text-xs text-gray-500">{msg.email}</div>
                    </td>
                    <td className="p-4 text-sm max-w-xs truncate">{msg.subject}</td>
                    <td className="p-4">
                      <div className="text-sm">{msg.phone}</div>
                    </td>
                    <td className="p-4">
                      {getStatusBadge(msg.status)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewMessage(msg)}
                          className="p-1 hover:bg-gray-200 rounded"
                          title="View"
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="p-1 hover:bg-red-100 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredMessages.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg">No messages found</p>
          </div>
        )}

        {/* Pagination */}
        <div className="p-4 border-t flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing {filteredMessages.length} of {total} messages
          </p>
          <div className="flex gap-2">
            <button 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button className="px-3 py-1 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]">
              {page}
            </button>
            <button 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Message View Modal - Full screen overlay with inline styles */}
      {showMessageModal && selectedMessage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 0,
            margin: 0
          }}
          onClick={handleCloseModal}
        >
          {/* Click on modal content to stop propagation (prevent closing) */}
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C46A1B]/10 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#C46A1B]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#111111]">Message Details</h3>
                  <p className="text-sm text-gray-500">From: {selectedMessage.name}</p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-4">
              {/* Sender Information */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-400">Name</p>
                    <p className="font-medium">{selectedMessage.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Email</p>
                    <p className="font-medium">{selectedMessage.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Phone</p>
                    <p className="font-medium">{selectedMessage.phone}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Date</p>
                    <p className="font-medium">{formatDate(selectedMessage.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div className="mb-3">
                <p className="text-xs text-gray-400 mb-1">Subject</p>
                <p className="font-medium bg-[#C46A1B]/5 border border-[#C46A1B]/20 rounded px-3 py-2">{selectedMessage.subject}</p>
              </div>

              {/* Message */}
              <div className="mb-3">
                <p className="text-xs text-gray-400 mb-1">Message</p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-700 whitespace-pre-wrap text-sm">{selectedMessage.message}</p>
                </div>
              </div>

              {/* Status */}
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2">Status</p>
                {getStatusBadge(selectedMessage.status)}
              </div>

              {/* Quick Reply Buttons */}
              <div className="flex gap-2">
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#C46A1B] text-white px-3 py-2 rounded-lg hover:bg-[#9E4E10] text-sm"
                >
                  <Mail className="w-4 h-4" />
                  Reply via Email
                </a>
                <a
                  href={`tel:${selectedMessage.phone}`}
                  className="flex-1 flex items-center justify-center gap-2 border border-[#C46A1B] text-[#C46A1B] px-3 py-2 rounded-lg hover:bg-[#C46A1B] hover:text-white text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Call Now
                </a>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-between shrink-0">
              <button
                onClick={() => handleMarkAsReplied(selectedMessage.id)}
                disabled={selectedMessage.status === 'replied'}
                className={`px-4 py-2 rounded-lg text-sm transition ${
                  selectedMessage.status === 'replied'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {selectedMessage.status === 'replied' ? 'Replied' : 'Mark as Replied'}
              </button>
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
